import {Badge} from '@dropbox/dig-components/badge';
import {Menu} from '@dropbox/dig-components/menu';
import {atoms, Box, withShade} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {NotificationLine} from '@dropbox/dig-icons/assets';
import {useQuery} from '@tanstack/react-query';
import {totalNotificationsAtom} from 'atoms/notifications';
import {NotificationService} from 'client';
import {useAtom} from 'jotai';
import {useEffect, useState} from 'react';
import {getService} from 'utilities';

import {NotificationCenter} from './NotificationCenter';
import {useVisibleNotifications} from './utils/useVisibleNotifications';

export const NotificationButton = () => {
  const [actionItems, setTotalNotifications] = useAtom(totalNotificationsAtom);
  const [isOpen, setIsOpen] = useState(false);
  const [isWindowFocused, setIsWindowFocused] = useState(true);

  useEffect(() => {
    const handleFocus = () => setIsWindowFocused(true);
    const handleBlur = () => setIsWindowFocused(false);

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  const {data} = useQuery({
    queryKey: ['notifications'],
    queryFn: getService(NotificationService).checkApiV1NotificationsGet,
    refetchInterval: isWindowFocused ? 1000 * 30 : false, // Refetch every 30 seconds when window is focused
    staleTime: 0,
  });
  const {actionItems: visibleActionItems, notifications: visibleNotifications} =
    useVisibleNotifications(data);

  useEffect(() => {
    setTotalNotifications(visibleActionItems.length);
  }, [setTotalNotifications, visibleActionItems.length]);

  const allNotifications = visibleNotifications.length + visibleActionItems.length;

  return (
    <Menu.Wrapper closeOnSelection onToggle={(menu) => setIsOpen(menu.isOpen)} isPortaled={false}>
      {({getContentProps, getTriggerProps, closeMenu}) => (
        <>
          <button
            {...getTriggerProps()}
            {...withShade({
              direction: 'up',
              style: {borderRadius: 20, paddingTop: 3, paddingBottom: 3},
              className: atoms({
                color: isOpen || allNotifications ? 'Text Base' : 'Text Subtle',
                cursor: 'pointer',
                backgroundColor: isOpen ? 'Opacity Surface' : 'Background Base',
                borderStyle: 'Solid',
                borderColor: isOpen ? 'Opacity Surface' : 'Border Subtle',
                borderWidth: allNotifications ? '1' : '0',
                outlineColor: 'Focus Ring',
                outlineOffset: '1',
                paddingX: '4',
              }),
            })}
          >
            <Box display="flex" alignItems="center">
              <UIIcon src={NotificationLine} />
              {actionItems ? (
                <Badge tone="alert" className={atoms({marginRight: '2'})}>
                  {actionItems}
                </Badge>
              ) : allNotifications ? (
                <Box
                  backgroundColor="Alert Base"
                  marginRight="2"
                  style={{width: 14, height: 14, borderRadius: '100%'}}
                />
              ) : null}
            </Box>
          </button>

          <Menu.Content
            {...getContentProps()}
            style={{width: 440, maxWidth: '100%', minHeight: 386, height: '100%'}}
          >
            <NotificationCenter handleSelection={() => closeMenu({})} />
          </Menu.Content>
        </>
      )}
    </Menu.Wrapper>
  );
};
